import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    TextField,
    Button,
    CircularProgress,
    Alert,
    InputAdornment,
    IconButton,
    Link
} from '@mui/material';
import {
    Visibility,
    VisibilityOff
} from '@mui/icons-material';
import apiClient from '../api'; 
import { useAuth } from '../context/AuthContext'; // Import useAuth to log user in
import { Helmet } from 'react-helmet';
import PasswordValidator from '../components/PasswordValidator';

function ResetPasswordPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { setAuthAfterReset } = useAuth(); // Get setAuthAfterReset function from context

    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [passwordValidation, setPasswordValidation] = useState({ isValid: false, strength: 0 });

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleClickShowPasswordConfirm = () => setShowPasswordConfirm(!showPasswordConfirm);
    const handleMouseDownPassword = (event) => event.preventDefault();

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!token) {
            setError('Invalid or missing reset token.');
            return;
        }
        if (password !== passwordConfirm) {
            setError('Passwords do not match.');
            return;
        }
        if (!passwordValidation.isValid) {
            setError('Password does not meet security requirements.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const response = await apiClient.patch(`/auth/reset-password/${token}`, { 
                password,
                passwordConfirm
            });

            setSuccessMessage('Password has been set successfully! Logging you in...');

            const loggedInUser = response.data?.data?.user;
            if (response.data.status === 'success' && loggedInUser) {
                const result = setAuthAfterReset(loggedInUser);

                if (result.success) {
                    setTimeout(() => {
                        navigate('/');
                    }, 1500);
                } else {
                    throw new Error('Failed to set authentication state');
                }
            } else {
                 throw new Error('Login after reset failed. Please log in manually.');
            }

        } catch (err) {
            console.error("Reset Password Error:", err);
            setError(err?.response?.data?.message || err.message || 'Failed to reset password.');
            setLoading(false);
        }
        // No finally setLoading(false) needed if navigating away on success
    };

    return (
        <Container component="main" maxWidth="xs">
            <Helmet>
                <title>Kranawetter Driver Portal | Set New Password</title>
                <meta name="description" content="Set a new password for Kranawetter Driver Portal - The trucking management platform that helps fleet operators organize customers, track documentation, and manage safety information." />
                <meta property="og:title" content="Kranawetter Driver Portal | Password Reset" />
                <meta property="og:description" content="Complete your password reset for Kranawetter Driver Portal's trucking fleet management software." />
                <meta property="og:type" content="website" />
                <meta name="robots" content="noindex" />
            </Helmet>
            <Paper elevation={3} sx={{ marginTop: { xs: 2, sm: 4 }, padding: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
                    Reset Your Password
                </Typography>
                
                {!successMessage ? (
                    <>
                        <Typography variant="body2" sx={{ mb: 1, textAlign: 'center' }}>
                            Enter and confirm your new password below.
                        </Typography>
                        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 0.5, width: '100%' }}>
                            {error && <Alert severity="error" sx={{ mb: 1, width: '100%', py: 0.5 }}>{error}</Alert>}
                            <TextField
                                margin="dense"
                                required
                                fullWidth
                                name="password"
                                label="New Password"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end" disabled={loading} size="small">
                                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                margin="dense"
                                required
                                fullWidth
                                name="passwordConfirm"
                                label="Confirm New Password"
                                type={showPasswordConfirm ? 'text' : 'password'}
                                id="passwordConfirm"
                                value={passwordConfirm}
                                onChange={(e) => setPasswordConfirm(e.target.value)}
                                disabled={loading}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton aria-label="toggle confirm password visibility" onClick={handleClickShowPasswordConfirm} onMouseDown={handleMouseDownPassword} edge="end" disabled={loading} size="small">
                                                {showPasswordConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {password && (
                                <PasswordValidator 
                                    password={password}
                                    onValidationChange={setPasswordValidation}
                                />
                            )}
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 1.5, mb: 1, py: 1 }}
                                disabled={loading || !password || !passwordConfirm}
                                size="medium"
                            >
                                {loading ? <CircularProgress size={20} color="inherit" /> : 'Reset Password'}
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Alert severity="success" sx={{ width: '100%', mt: 1, py: 0.75 }}>
                        {successMessage}
                    </Alert>
                )}

                {/* Optionally add a link back to login if they arrived here by mistake */}
                {!successMessage && (
                     <Typography variant="body2" sx={{ mt: 1 }}>
                         <RouterLink to="/login" style={{ textDecoration: 'none', color: 'inherit' }}>
                             Back to Sign In
                         </RouterLink>
                     </Typography>
                )}
            </Paper>
        </Container>
    );
}

export default ResetPasswordPage; 